import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

/** RFC 9457 Problem Details, as produced by the ASP.NET Core backend. */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  traceId?: string;
  /** Present on validation problems (400): field name (camelCase) → messages. */
  errors?: Record<string, string[]>;
}

export type ApiError = FetchBaseQueryError | SerializedError;

/** Normalized, UI-ready view of any RTK Query error. */
export interface ApiErrorInfo {
  /** HTTP status, or null for network/timeout/client errors. */
  status: number | null;
  message: string;
  fieldErrors: Record<string, string[]>;
  traceId?: string;
}

const STATUS_MESSAGES: Record<number, string> = {
  400: 'The request is invalid.',
  401: 'You are not signed in or your session has expired.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested resource was not found.',
  409: 'The request conflicts with existing data.',
  429: 'Too many attempts. Please wait a moment and try again.',
  500: 'An unexpected server error occurred.',
  503: 'The service is temporarily unavailable. Please try again later.',
};

export function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
  return typeof error === 'object' && error !== null && 'status' in error;
}

export function isProblemDetails(value: unknown): value is ProblemDetails {
  return (
    typeof value === 'object' &&
    value !== null &&
    ('title' in value || 'status' in value || 'errors' in value)
  );
}

function httpStatusOf(error: FetchBaseQueryError): number | null {
  if (typeof error.status === 'number') return error.status;
  if (error.status === 'PARSING_ERROR') return error.originalStatus;
  return null;
}

/**
 * Converts an RTK Query error into a message and field errors.
 * `overrides` customize messages per HTTP status for a specific call site.
 */
export function parseApiError(
  error: unknown,
  overrides: Partial<Record<number, string>> = {},
): ApiErrorInfo {
  if (!isFetchBaseQueryError(error)) {
    const message =
      typeof error === 'object' && error !== null && 'message' in error
        ? String(error.message)
        : 'An unexpected error occurred.';
    return { status: null, message, fieldErrors: {} };
  }

  switch (error.status) {
    case 'FETCH_ERROR':
      return {
        status: null,
        message: 'Cannot reach the server. Check your connection and try again.',
        fieldErrors: {},
      };
    case 'TIMEOUT_ERROR':
      return { status: null, message: 'The server took too long to respond.', fieldErrors: {} };
    case 'CUSTOM_ERROR':
      return { status: null, message: error.error, fieldErrors: {} };
  }

  const status = httpStatusOf(error);
  const problem = isProblemDetails(error.data) ? error.data : undefined;
  const fallback = status === null ? 'An unexpected error occurred.' : STATUS_MESSAGES[status];
  const override = status === null ? undefined : overrides[status];
  // Title is the summary ("Data service is unavailable."), detail the specifics ("Please try again later.").
  const serverMessage = [problem?.title, problem?.detail].filter(Boolean).join(' ') || undefined;

  return {
    status,
    message: override ?? serverMessage ?? fallback ?? `Request failed (${status}).`,
    fieldErrors: problem?.errors ?? {},
    ...(problem?.traceId ? { traceId: problem.traceId } : {}),
  };
}

/** `data.kind` of the error a request returns when its session expired and could not be renewed. */
export const SESSION_RENEWAL_ERROR = 'session-renewal';

/** Why renewal failed, from the HTTP status of `POST /api/auth/refresh` (null: no response). */
export function sessionRenewalMessage(refreshStatus: number | null): string {
  const prefix = 'Your session expired and could not be renewed:';
  if (refreshStatus === null) return `${prefix} the server cannot be reached.`;
  if (refreshStatus === 404) {
    return (
      `${prefix} the server has no session-renewal endpoint (POST /api/auth/refresh returned ` +
      '404 Not Found). The API is probably running an outdated version.'
    );
  }
  if (refreshStatus >= 500) return `${prefix} the server failed (${refreshStatus}).`;
  return `${prefix} the server answered ${refreshStatus}.`;
}

/** The request failed because the session expired and renewing it failed (see `sessionRenewalMessage`). */
export function isSessionRenewalError(error: unknown): boolean {
  return (
    isFetchBaseQueryError(error) &&
    error.status === 'CUSTOM_ERROR' &&
    typeof error.data === 'object' &&
    error.data !== null &&
    'kind' in error.data &&
    error.data.kind === SESSION_RENEWAL_ERROR
  );
}

export function getErrorMessage(error: unknown, overrides?: Partial<Record<number, string>>) {
  return parseApiError(error, overrides).message;
}

export function getHttpStatus(error: unknown): number | null {
  return isFetchBaseQueryError(error) ? httpStatusOf(error) : null;
}
